/**
 * Device selection. The functions are pure and run under vitest with no
 * DOM; `DeviceList` wraps them with enumerateDevices and the devicechange
 * event, and the media controller with storage and the live tracks.
 */

import { patch, readonly, writable, type ReadableStore } from '../store.js'

export type DeviceKind = 'audioinput' | 'videoinput' | 'audiooutput'

export interface DeviceOption {
  deviceId: string
  label: string
  kind: DeviceKind
  groupId: string
}

export interface DevicePrefs {
  mic: string | null
  camera: string | null
  speaker: string | null
}

/**
 * Windows exposes two virtual devices through Chromium. `default` follows the
 * OS "default device"; `communications` follows the OS "default communications
 * device". Plugging in a headset — Bluetooth especially — usually moves only
 * the second, which is why a call keeps using the laptop mic after the
 * headset is on. Teams and Zoom bind to the communications device for exactly
 * this reason, and so do we when nobody has chosen: it is the device Windows
 * itself designates for calls. No other platform has the id, and asking for
 * it as `ideal` rather than `exact` costs nothing there.
 */
export const COMMUNICATIONS_DEVICE_ID = 'communications'

const KIND_NOUN: Record<DeviceKind, string> = {
  audioinput: 'Microphone',
  videoinput: 'Camera',
  audiooutput: 'Speaker',
}

/**
 * A device's name as a person would say it. The browser passes on what the
 * driver reports, and drivers report for engineers: "HP HD Camera
 * (0408:5347)", "Microphone Array (Intel® Smart Sound Technology (Intel®
 * SST))". The USB ids, the trademark signs and the brackets inside brackets
 * go; the name and its maker stay, so two devices can still be told apart.
 */
export function friendlyLabel(label: string): string {
  let out = label.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)/gi, '').replace(/[®™©]/g, '')
  // Innermost brackets first, while a bracket still holds another one.
  for (let i = 0; i < 3 && /\([^()]*\([^()]*\)[^()]*\)/.test(out); i++)
    out = out.replace(/\s*\([^()]*\)(?=[^()]*\))/g, '')
  return out
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/** Labels are empty until permission has been granted once; number them meanwhile. */
export function labelFor(device: Pick<DeviceOption, 'label' | 'kind'>, index: number): string {
  return friendlyLabel(device.label) || `${KIND_NOUN[device.kind]} ${index + 1}`
}

export interface GroupedDevices {
  mics: DeviceOption[]
  cameras: DeviceOption[]
  speakers: DeviceOption[]
}

/**
 * Sort enumerateDevices() output into the three lists the picker shows.
 *
 * Before permission, Chromium returns one placeholder per kind with an empty
 * id. Those cannot be selected and are dropped; the real list arrives after
 * the first successful getUserMedia.
 */
export function groupDevices(
  list: ReadonlyArray<Pick<MediaDeviceInfo, 'deviceId' | 'label' | 'kind' | 'groupId'>>,
): GroupedDevices {
  const out: GroupedDevices = { mics: [], cameras: [], speakers: [] }
  for (const d of list) {
    if (!d.deviceId) continue
    const bucket =
      d.kind === 'audioinput' ? out.mics : d.kind === 'videoinput' ? out.cameras : out.speakers
    bucket.push({
      deviceId: d.deviceId,
      label: labelFor(d, bucket.length),
      kind: d.kind,
      groupId: d.groupId,
    })
  }
  return out
}

/**
 * Which device to use when the user has not picked one in this session.
 *
 *   1. What they picked last time, if it is still plugged in.
 *   2. The OS's communications device, on the one platform that has one.
 *   3. null — let the browser choose.
 */
export function preferredDevice(
  options: ReadonlyArray<DeviceOption>,
  stored: string | null,
): string | null {
  if (stored && options.some((o) => o.deviceId === stored)) return stored
  if (options.some((o) => o.deviceId === COMMUNICATIONS_DEVICE_ID)) return COMMUNICATIONS_DEVICE_ID
  return null
}

export function labelOf(options: ReadonlyArray<DeviceOption>, deviceId: string): string {
  return options.find((o) => o.deviceId === deviceId)?.label ?? 'another device'
}

const STORAGE_KEYS: Record<keyof DevicePrefs, string> = {
  mic: 'ls_device_mic',
  camera: 'ls_device_camera',
  speaker: 'ls_device_speaker',
}

type PrefStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/**
 * Storage can be missing or throw — private windows, blocked site data — and
 * a device preference is never worth an error. Absent storage means no
 * preference, and a write that fails is simply not remembered.
 */
export function loadDevicePrefs(storage: PrefStorage | null): DevicePrefs {
  const read = (key: string): string | null => {
    try {
      return storage?.getItem(key) ?? null
    } catch {
      return null
    }
  }
  return {
    mic: read(STORAGE_KEYS.mic),
    camera: read(STORAGE_KEYS.camera),
    speaker: read(STORAGE_KEYS.speaker),
  }
}

export function saveDevicePref(
  storage: PrefStorage | null,
  kind: keyof DevicePrefs,
  deviceId: string | null,
): void {
  try {
    if (deviceId) storage?.setItem(STORAGE_KEYS[kind], deviceId)
    else storage?.removeItem(STORAGE_KEYS[kind])
  } catch {
    // Not remembered. The call goes on with the device that was chosen.
  }
}

export interface DeviceListState {
  mics: DeviceOption[]
  cameras: DeviceOption[]
  speakers: DeviceOption[]
}

/**
 * Enumerate devices and follow plug and unplug.
 *
 * Labels — and on Chromium, ids — are only complete after getUserMedia has
 * been granted once, so refresh() is worth calling again right after the
 * first successful start. The media controller does.
 */
export class DeviceList {
  private readonly store = writable<DeviceListState>({ mics: [], cameras: [], speakers: [] })
  readonly state: ReadableStore<DeviceListState> = readonly(this.store)

  /**
   * Output selection is setSinkId, which Chromium implements and Safari does
   * not. Where it is missing the OS decides, and the picker says so rather
   * than offering a list that would do nothing.
   */
  readonly supportsSpeakerSelection =
    typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype

  async refresh(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return
    try {
      const grouped = groupDevices(await navigator.mediaDevices.enumerateDevices())
      patch(this.store, {
        mics: grouped.mics,
        cameras: grouped.cameras,
        speakers: this.supportsSpeakerSelection ? grouped.speakers : [],
      })
    } catch {
      // An enumeration failure leaves the previous list standing. Better a
      // stale picker than an empty one mid-call.
    }
  }

  /** Refresh on every plug or unplug, then tell the caller. Returns the unsubscribe. */
  follow(onChange: () => void): () => void {
    const md = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices
    if (!md?.addEventListener) return () => {}
    const handler = (): void => {
      void this.refresh().then(onChange)
    }
    md.addEventListener('devicechange', handler)
    return () => md.removeEventListener('devicechange', handler)
  }
}
